import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Portal, generateTestIds} from '@homebase/common';
import CloseButton from '../CloseButton';
import cx from 'classnames';
import './Toast.$BRAND.css.js';

const noop = () => {};
// eslint-disable-next-line react/no-unsafe
class Toast extends Component {
  static propTypes = {
    // Offset from the bottom of the window, in px
    bottomOffset: PropTypes.number,
    // Custom text for the close icon. Non-visual, accessible text
    dismissText: PropTypes.string,
    // Time in milliseconds the toast appears on screen
    duration: PropTypes.number,
    // If true, toast is open/active
    isOpen: PropTypes.bool,
    // Handler called when toast is closed - either manually or after the timeout
    onRequestClose: PropTypes.func,
    // If true, toast will persist until user clicks the dismiss icon
    manualDismiss: PropTypes.bool,
    // Event name for click tracking. If set, adds `data-event-name="..."` to the dismiss button
    dataEventName: PropTypes.string,
    // Group name for click tracking. If set, adds `data-group-name="..."` to the dismiss button
    dataGroupName: PropTypes.string,
    // Content for the toast
    children: PropTypes.node.isRequired,
    // Optional Enzyme and Codeception testing identifiers
    testIds: PropTypes.shape({
      codeception: PropTypes.string,
      enzyme: PropTypes.string,
    }),
  };

  static defaultProps = {
    bottomOffset: 100,
    dismissText: null,
    duration: 6000,
    isOpen: false,
    manualDismiss: false,
    dataEventName: null,
    dataGroupName: null,
    onRequestClose: noop,
    testIds: {
      enzyme: 'toast',
    },
  };

  state = {
    isOpen: this.props.isOpen,
  };

  componentDidMount() {
    if (this.props.isOpen) {
      this.handleToastOpen();
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({isOpen: nextProps.isOpen}, () => {
      this.clearToastTimeout();
      if (nextProps.isOpen) {
        this.handleToastOpen();
      }
    });
  }

  componentWillUnmount() {
    // Since setState will be called when the toast timeout completes, make sure the timeout is cleared if the component unmounts before
    this.clearToastTimeout();
  }

  /**
   * Handle focusing dismissible toast, or set the timeout for the auto-closing toast whenever the toast is opened
   */
  handleToastOpen = () => {
    if (this.props.manualDismiss && this.toastRef) {
      this.toastRef.focus();
    }
    if (!this.props.manualDismiss) {
      this.closeWithTimeout();
    }
  };

  clearToastTimeout = () => {
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
    }
  };

  /**
   * Automatically close toast without user interaction - after {this.props.duration}
   * Then call onRequestClose so the parent can know about it
   */
  closeWithTimeout = () => {
    this.activeTimeout = setTimeout(
      () =>
        this.setState({isOpen: false}, () => {
          this.activeTimeout = null;
          if (this.props.onRequestClose) {
            this.props.onRequestClose();
          }
        }),
      this.props.duration
    );
  };

  /**
   * If toast needs to be dismissed manually, it can be handled in the
   * parent component with `onRequestClose`
   */
  requestClose = (e) => {
    if (this.props.onRequestClose && this.props.manualDismiss) {
      this.props.onRequestClose(e);
    } else {
      e.preventDefault();
    }
  };

  render() {
    const toastPositioning = {
      bottom: this.props.bottomOffset,
    };

    return (
      <Portal isOpen={this.state.isOpen}>
        <div
          className={cx('Toast Toast-transition--fadeInBottomCss', {
            'is-dismissible': this.props.manualDismiss,
          })}
          style={toastPositioning}
          role="alert"
          tabIndex="-1"
          aria-labelledby="toast-message"
          ref={(c) => (this.toastRef = c)}
          data-hb-id="Toast"
          {...generateTestIds({testIds: this.props.testIds})}
        >
          <div className="Toast-message" id="toast-message">
            {this.props.children}
          </div>
          {this.props.manualDismiss && (
            <div className="Toast-close">
              <CloseButton
                variation="inverted"
                onClick={this.requestClose}
                dataEventName={
                  this.props.dataEventName || this.props.dataGroupName
                }
                label={this.props.dismissText}
                data-event-name={this.props.dataEventName}
                data-group-name={this.props.dataGroupName}
                data-enzyme-id="toastDismiss"
              />
            </div>
          )}
        </div>
      </Portal>
    );
  }
}

export default Toast;
